import React, { useEffect, useState } from 'react';
import DashboardLayout from './DashboardLayout';
import { Card, Row, Col, Container, Spinner, Alert, Button, ButtonGroup } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Line, Bar } from 'react-chartjs-2';
import { 
  Chart, 
  LineElement, 
  PointElement, 
  LinearScale, 
  CategoryScale, 
  Legend, 
  Tooltip,
  BarElement,
  Title,
  Filler
} from 'chart.js';
import { useAuth } from '../context/AuthContext';
import { businessApi, API_BASE_URL } from '../services/api';
import { 
  FaChartLine, 
  FaMousePointer, 
  FaUserPlus, 
  FaBullhorn,
  FaEye,
  FaBuilding,
  FaCalendarAlt,
  FaInfoCircle
} from 'react-icons/fa';
import './DashboardAnalytics.css';

Chart.register(
  LineElement, 
  PointElement, 
  LinearScale, 
  CategoryScale, 
  Legend, 
  Tooltip,
  BarElement,
  Title,
  Filler
);

const DashboardAnalytics = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState([]);
  const [range, setRange] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [chartType, setChartType] = useState('line');

  useEffect(() => {
    const fetchBusinesses = async () => {
      if (!user) return;
      try {
        const allBusinesses = await businessApi.fetchAll();
        const myBusinesses = allBusinesses.filter(
          b => String(b.userId) === String(user.id) ||
               String(b.userid) === String(user.id) ||
               String(b.ownerId) === String(user.id) ||
               String(b.ownerid) === String(user.id)
        );
        setBusinesses(myBusinesses);
        if (myBusinesses.length > 0) setSelectedBusinessId(myBusinesses[0].id);
      } catch (err) {
        setError('Failed to load businesses');
      }
    };
    fetchBusinesses();
  }, [user]);

  useEffect(() => {
    if (!selectedBusinessId) return;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE_URL}/get_analytics.php?business_id=${selectedBusinessId}&range=${range}`)
      .then(res => res.json())
      .then(data => {
        setStats(data.data || []);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load analytics data');
        setLoading(false);
      });
  }, [selectedBusinessId, range]);

  // Calculate summary statistics
  const calculateTotal = (type) => {
    return stats
      .filter(s => s.event_type === type)
      .reduce((sum, s) => sum + Number(s.total || 0), 0);
  };

  const totalViews = calculateTotal('view');
  const totalClicks = calculateTotal('click');
  const totalLeads = calculateTotal('lead');
  const totalCta = calculateTotal('cta');

  // Prepare data for Chart.js
  const periods = [...new Set(stats.map(s => s.period))].sort();
  const eventTypes = ['view', 'click', 'lead', 'cta'];
  
  const colors = {
    view: { 
      border: '#667eea', 
      background: 'rgba(102, 126, 234, 0.1)',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    click: { 
      border: '#f6ad55', 
      background: 'rgba(246, 173, 85, 0.1)',
      gradient: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)'
    },
    lead: { 
      border: '#48bb78', 
      background: 'rgba(72, 187, 120, 0.1)',
      gradient: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'
    },
    cta: { 
      border: '#f56565', 
      background: 'rgba(245, 101, 101, 0.1)',
      gradient: 'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)'
    }
  };

  const datasets = eventTypes.map(type => ({
    label: type.charAt(0).toUpperCase() + type.slice(1),
    data: periods.map(period => {
      const found = stats.find(s => s.event_type === type && s.period === period);
      return found ? Number(found.total) : 0;
    }),
    fill: true,
    borderColor: colors[type].border,
    backgroundColor: colors[type].background,
    borderWidth: 3,
    tension: 0.4,
    pointRadius: 5,
    pointHoverRadius: 7,
    pointBackgroundColor: colors[type].border,
    pointBorderColor: '#fff',
    pointBorderWidth: 2
  }));

  const chartData = {
    labels: periods,
    datasets
  };

  const barChartData = {
    labels: periods,
    datasets: eventTypes.map(type => ({
      label: type.charAt(0).toUpperCase() + type.slice(1),
      data: periods.map(period => {
        const found = stats.find(s => s.event_type === type && s.period === period);
        return found ? Number(found.total) : 0;
      }),
      backgroundColor: colors[type].border,
      borderColor: colors[type].border,
      borderWidth: 2,
      borderRadius: 8
    }))
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12,
            weight: '600',
            family: "'Inter', sans-serif"
          },
          color: '#4a5568'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: '600'
        },
        bodyFont: {
          size: 13
        },
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(226, 232, 240, 0.5)',
          drawBorder: false
        },
        ticks: {
          color: '#718096',
          font: {
            size: 11,
            family: "'Inter', sans-serif"
          },
          padding: 10
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#718096',
          font: {
            size: 11,
            family: "'Inter', sans-serif"
          },
          padding: 10
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  const selectedBusiness = businesses.find(b => b.id === selectedBusinessId);

  const metricCards = [
    {
      icon: FaEye,
      label: 'Total Views',
      value: totalViews,
      color: colors.view,
      delay: 0.1
    },
    {
      icon: FaMousePointer,
      label: 'Total Clicks',
      value: totalClicks,
      color: colors.click,
      delay: 0.2
    },
    {
      icon: FaUserPlus,
      label: 'Total Leads',
      value: totalLeads,
      color: colors.lead,
      delay: 0.3
    },
    {
      icon: FaBullhorn,
      label: 'CTA Conversions',
      value: totalCta,
      color: colors.cta,
      delay: 0.4
    }
  ];

  return (
    <DashboardLayout>
      <div className="analytics-container">
        <Container fluid className="p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header Section */}
            <div className="analytics-header">
              <div>
                <h1 className="analytics-title">Analytics & Insights</h1>
                <p className="analytics-subtitle">Track and analyze your business performance metrics</p>
              </div>
              {selectedBusiness && (
                <div className="selected-business-badge">
                  <FaBuilding className="me-2" />
                  {selectedBusiness.name || selectedBusiness.businessName}
                </div>
              )}
            </div>

            {/* Controls Section */}
            <Card className="analytics-controls-card mb-4">
              <Card.Body>
                <Row className="align-items-center g-3">
                  {businesses.length > 1 && (
                    <Col md={4} sm={6} xs={12}>
                      <label className="control-label">
                        <FaBuilding className="me-2" />
                        Select Business
                      </label>
                      <select 
                        className="form-control analytics-select"
                        value={selectedBusinessId || ''} 
                        onChange={e => setSelectedBusinessId(e.target.value)}
                      >
                        {businesses.map(biz => (
                          <option key={biz.id} value={biz.id}>
                            {biz.name || biz.businessName}
                          </option>
                        ))}
                      </select>
                    </Col>
                  )}
                  <Col md={4} sm={6} xs={12}>
                    <label className="control-label">
                      <FaCalendarAlt className="me-2" />
                      Time Range
                    </label>
                    <ButtonGroup className="w-100">
                      {['daily', 'weekly', 'monthly'].map(r => (
                        <Button
                          key={r}
                          variant={range === r ? 'primary' : 'outline-primary'}
                          onClick={() => setRange(r)}
                          className="range-btn"
                        >
                          {r.charAt(0).toUpperCase() + r.slice(1)}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </Col>
                  <Col md={4} sm={6} xs={12}>
                    <label className="control-label">
                      <FaChartLine className="me-2" />
                      Chart Type
                    </label>
                    <ButtonGroup className="w-100">
                      <Button
                        variant={chartType === 'line' ? 'primary' : 'outline-primary'}
                        onClick={() => setChartType('line')}
                        className="chart-type-btn"
                      >
                        Line
                      </Button>
                      <Button
                        variant={chartType === 'bar' ? 'primary' : 'outline-primary'}
                        onClick={() => setChartType('bar')}
                        className="chart-type-btn"
                      >
                        Bar
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Metrics Cards */}
            <Row className="mb-4 g-4">
              {metricCards.map((metric, index) => (
                <Col key={index} md={3} sm={6} xs={12}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: metric.delay, duration: 0.4 }}
                  >
                    <Card className="analytics-metric-card">
                      <Card.Body>
                        <div className="metric-icon-wrapper" style={{ background: metric.color.gradient }}>
                          <metric.icon className="metric-icon" />
                        </div>
                        <h2 className="metric-value">{metric.value.toLocaleString()}</h2>
                        <p className="metric-label">{metric.label}</p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>

            {/* Chart Section */}
            {loading ? (
              <Card className="analytics-chart-card">
                <Card.Body>
                  <div className="loading-container">
                    <Spinner animation="border" className="loading-spinner" />
                    <p className="mt-3 text-muted">Loading analytics data...</p>
                  </div>
                </Card.Body>
              </Card>
            ) : error ? (
              <Alert variant="danger" className="error-alert">
                {error}
              </Alert>
            ) : periods.length === 0 ? (
              <Card className="analytics-chart-card">
                <Card.Body>
                  <div className="empty-state">
                    <FaChartLine size={64} className="text-muted mb-3" />
                    <h5 className="text-muted">No Data Available</h5>
                    <p className="text-muted">Analytics data will appear here once you start receiving traffic.</p>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <motion.div
                key={chartType}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <Card className="analytics-chart-card">
                  <Card.Header className="analytics-chart-header">
                    <h5 className="mb-0">Performance Overview</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="chart-container">
                      {chartType === 'line' ? (
                        <Line data={chartData} options={chartOptions} />
                      ) : (
                        <Bar data={barChartData} options={chartOptions} />
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            )}

            {/* Legend/Info Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <Card className="analytics-info-card mt-4">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <FaInfoCircle className="me-2 text-primary" size={20} />
                    <h5 className="mb-0">Understanding Your Metrics</h5>
                  </div>
                  <Row className="g-3">
                    {[
                      { label: 'Views', desc: 'Total page visits and impressions', color: colors.view },
                      { label: 'Clicks', desc: 'Phone number and email link clicks', color: colors.click },
                      { label: 'Leads', desc: 'Form submissions and inquiries', color: colors.lead },
                      { label: 'CTA', desc: 'Call-to-action button conversions', color: colors.cta }
                    ].map((item, index) => (
                      <Col key={index} md={3} sm={6} xs={12}>
                        <div className="info-item">
                          <div className="info-color-indicator" style={{ background: item.color.border }}></div>
                          <div>
                            <strong>{item.label}</strong>
                            <p className="mb-0 text-muted small">{item.desc}</p>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            </motion.div>
          </motion.div>
        </Container>
      </div>
    </DashboardLayout>
  );
};

export default DashboardAnalytics; 